import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const MessageDrawer = ({ 
  isOpen, 
  onClose, 
  client, 
  messages, 
  currentUser,
  onSendMessage 
}) => {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  // Scroll automatique en bas quand les messages changent
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (!isOpen || !client) return null;

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    const messageData = {
      client_id: client.id,
      content: newMessage,
      recipient_id: client.sponsor_id,
      is_read: false
    };

    const sent = await onSendMessage(messageData);
    if (sent) setNewMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return format(date, "dd/MM/yyyy 'à' HH'h'mm");
  };

  const canSendMessage = ['pending', 'refund', 'payed'].includes(client.status);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 flex z-[60]"
        >
          <div className="flex-grow bg-black/60 backdrop-blur-sm" onClick={onClose} />
          
          <motion.div 
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white w-full md:w-[clamp(380px,32vw,640px)] h-full relative flex flex-col border-l border-gray-200"
          >
        {/* Header du chat */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-fideliz-blue-bg text-fideliz-blue rounded-full flex items-center justify-center font-semibold text-sm">
              {client.sponsor_name?.split(' ').map(n => n[0]).join('') || 'P'}
            </div>
            <div>
              <h2 className="text-base font-semibold text-fideliz-dark">
                Discussion avec {client.sponsor_name || 'le parrain'}
              </h2>
              <p className="text-xs text-gray-500">
                Au sujet de {client.first_name} {client.last_name}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="w-10 h-10 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-grow overflow-y-auto bg-gray-50 p-6 scrollbar-hidden">
          <div className="flex flex-col space-y-4">
            {messages?.map((message, index, array) => {
              const previousMessage = array[index - 1];
              const showSender = !previousMessage || previousMessage.sender_id !== message.sender_id;
              const isCurrentUser = message.sender_id === currentUser.id;

              return (
                <div key={message.id}>
                  <div className={`flex flex-col ${isCurrentUser ? 'items-end' : 'items-start'}`}>
                    {showSender && (
                      <p className="text-xs font-medium text-gray-500 mb-1.5 px-1">
                        {isCurrentUser ? 'Vous' : client?.sponsor_name || 'Parrain'}
                      </p>
                    )}
                    <div className={`py-3 px-4 rounded-2xl max-w-[75%] ${
                      isCurrentUser 
                        ? 'bg-fideliz-blue text-white' 
                        : 'bg-white border border-gray-200'
                    }`}>
                      <p className={`text-sm leading-relaxed ${isCurrentUser ? 'text-white' : 'text-gray-900'}`}>
                        {message.content}
                      </p>
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5 px-1">
                      {formatDateTime(message.created_at)}
                    </p>
                  </div>

                  {message.type === 'request' && isCurrentUser && (
                    <p className="text-center mt-4 mb-2 text-sm italic">
                      Une demande de rejet du client a été demandée.
                      <br />
                      {client.user_info} a été informé, dans l'attente de sa décision.
                    </p>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        {canSendMessage && (
          <div className="px-6 py-4 border-t border-gray-100 bg-white flex-shrink-0">
            <div className="flex items-end gap-3">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 px-4 py-3 text-sm bg-gray-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-fideliz-blue focus:bg-white placeholder-gray-500 transition-all"
                placeholder="Écrire votre message..."
              />
              <button
                onClick={handleSend}
                disabled={!newMessage.trim()}
                className="px-5 py-3 bg-fideliz-blue text-white text-sm font-semibold rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              >
                Envoyer
              </button>
            </div>
          </div>
        )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MessageDrawer;

